import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const RoleHierarchySidebar = ({ staffMembers, className = '', onAddStaff }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const roleHierarchy = [
    {
      role: 'Administrator',
      color: 'bg-primary text-primary-foreground',
      icon: 'Crown',
      description: 'Full system access and business management',
      permissions: ['Business settings & Reporting','Staff, Category & Service Management']
    },
    {
      role: 'Scanner',
      color: 'bg-accent text-accent-foreground',
      icon: 'ScanBarcode',
      description: 'Creating bookings',
      permissions: ['Capturing customer & vehicle details', 'Creating bookings']
    },
    {
      role: 'Releaser',
      color: 'bg-secondary text-secondary-foreground',
      icon: 'Shield',
      description: 'Vehicle release and customer service',
      permissions: ['Release vehicles','Verify OTP for vehicle collection']
    },
    {
      role: 'Reporting',
      color: 'bg-muted text-muted-foreground',
      icon: 'FileChartColumn',
      description: 'Viewing business reports and performance metrics',
      permissions: ['Dashboard viewing', 'Generating reports' ]
    }
  ];

  const getStaffCountByRole = (role) => {
    return staffMembers?.filter(staff => (staff?.admin && role == 'Administrator') || (staff?.releasing && role == 'Releaser') || (staff?.reporting && role == 'Reporting') || (staff?.scanning && role == 'Scanner') && staff?.status)?.length;
  };

  const getTotalStaffCount = () => {
    return staffMembers?.filter(staff => staff?.active)?.length;
  };

  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-2">
          <Icon name="Users" size={20} className="text-primary" />
          {!isCollapsed && (
            <div>
              <h3 className="font-medium text-foreground">Role Hierarchy</h3>
              <p className="text-xs text-muted-foreground">Staff organization structure</p>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="h-8 w-8"
        >
          <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={16} />
        </Button>
      </div>
      {!isCollapsed && (
        <>
          {/* Staff Summary */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-foreground">Total Active Staff</span>
              <span className="text-lg font-bold text-primary">{getTotalStaffCount()}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((getTotalStaffCount() / staffMembers?.length) * 100, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {getTotalStaffCount()}/{staffMembers?.length} staff capacity
            </p>
          </div>

          {/* Role Hierarchy */}
          <div className="p-4 space-y-4">
            {roleHierarchy?.map((roleInfo, index) => {
              const staffCount = getStaffCountByRole(roleInfo?.role);
              
              return (
                <div key={roleInfo?.role} className="space-y-3">
                  {/* Role Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${roleInfo?.color}`}>
                        <Icon name={roleInfo?.icon} size={16} />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{roleInfo?.role}</div>
               
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-foreground">{staffCount}</div>
                      <div className="text-xs text-muted-foreground">active</div>
                    </div>
                  </div>
                  {/* Role Description */}
                  <div className="pl-11">
                    <p className="text-xs text-muted-foreground mb-2">{roleInfo?.description}</p>
                    
                    {/* Permissions */}
                    <div className="space-y-1">
                      {roleInfo?.permissions?.slice(0, 2)?.map((permission, permIndex) => (
                        <div key={permIndex} className="flex items-center space-x-2">
                          <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                          <span className="text-xs text-muted-foreground">{permission}</span>
                        </div>
                      ))}
                      {roleInfo?.permissions?.length > 2 && (
                        <div className="flex items-center space-x-2">
                          <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                          <span className="text-xs text-muted-foreground">
                            +{roleInfo?.permissions?.length - 2} more
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Hierarchy Connection */}
                  {index < roleHierarchy?.length - 1 && (
                    <div className="flex justify-center">
                      <div className="w-px h-4 bg-border"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-t border-border bg-muted/30">
            <h4 className="text-sm font-medium text-foreground mb-3">Quick Actions</h4>
            <div className="space-y-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs"
                iconName="UserPlus"
                iconPosition="left"
                iconSize={14}
                onClick={onAddStaff}
              >
                Add Staff Member
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs"
                iconName="FileText"
                iconPosition="left"
                iconSize={14}
                onClick={() => alert('Staff report generation coming soon!')}
              >
                Staff Report
              </Button>
            </div>
          </div>
        </>
      )}
      {/* Collapsed State */}
      {isCollapsed && (
        <div className="p-2 space-y-2">
          {roleHierarchy?.map((roleInfo) => {
            const staffCount = getStaffCountByRole(roleInfo?.role);
            
            return (
              <div key={roleInfo?.role} className="relative group">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${roleInfo?.color} relative`}>
                  <Icon name={roleInfo?.icon} size={16} />
                  {staffCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                      {staffCount}
                    </div>
                  )}
                </div>
                {/* Tooltip */}
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-2 bg-popover border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 delay-300 z-1001 whitespace-nowrap">
                  <div className="text-sm font-medium text-popover-foreground">{roleInfo?.role}</div>
                  <div className="text-xs text-muted-foreground">{staffCount} active staff</div>
                  <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-popover"></div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RoleHierarchySidebar;